import { zodResolver } from '@hookform/resolvers/zod';
import { BUSINESS_TYPES, LEAD_SOURCES, PRIORITIES, PRIORITY_LABELS, SOURCE_LABELS, organizationUpdateSchema } from '@takumi/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useInvalidateCrm, useUserOptions } from '@/lib/queries';
import type { OrgDetail } from '@/lib/types';

type In = z.input<typeof organizationUpdateSchema>;
type Out = z.output<typeof organizationUpdateSchema>;

const str = (v: string | null | undefined) => v ?? '';
const num = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

export function EditOrganizationDialog({ org, open, onOpenChange }: { org: OrgDetail; open: boolean; onOpenChange: (o: boolean) => void }) {
  const invalidate = useInvalidateCrm();
  const users = useUserOptions();
  const { register, handleSubmit, reset, formState, setError } = useForm<In, unknown, Out>({ resolver: zodResolver(organizationUpdateSchema) });
  const e = formState.errors;

  useEffect(() => {
    if (open) {
      reset({
        name: org.name,
        legalName: str(org.legalName),
        vatId: str(org.vatId),
        businessType: str(org.businessType),
        branchesCount: num(org.branchesCount) as never,
        address: str(org.address),
        city: str(org.city),
        website: str(org.website),
        source: org.source,
        priority: org.priority,
        ownerId: org.ownerId ?? '',
        estimatedValue: num(org.estimatedValue) as never,
        estimatedMonthlyKg: num(org.estimatedMonthlyKg) as never,
        paymentTerms: str(org.paymentTerms),
        shippingTerms: str(org.shippingTerms),
        defaultDiscountPct: num(org.defaultDiscountPct) as never,
        notes: str(org.notes),
      });
    }
  }, [open, org, reset]);

  const save = useMutation({
    mutationFn: (body: Out) => api.patch(`/organizations/${org.id}`, body),
    onSuccess: () => {
      invalidate(org.id);
      toast.success('הפרטים נשמרו');
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError) err.issues.forEach((i) => setError(i.path as never, { message: i.message }));
      toast.error(errorMessage(err));
    },
  });

  // Empty numeric inputs mean "clear the value".
  const onSubmit = handleSubmit((v) => {
    const clean = Object.fromEntries(Object.entries(v).map(([k, val]) => [k, typeof val === 'number' && Number.isNaN(val) ? null : val]));
    save.mutate(clean as Out);
  });
  const numField = (name: 'branchesCount' | 'estimatedValue' | 'estimatedMonthlyKg' | 'defaultDiscountPct') =>
    register(name, { setValueAs: (v) => (v === '' || v === null ? null : Number(v)) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="עריכת פרטים" description={org.name} wide>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <section className="grid gap-4 sm:grid-cols-2">
            <Field label="שם העסק" required error={e.name?.message}>{(id) => <Input id={id} {...register('name')} />}</Field>
            <Field label="שם משפטי">{(id) => <Input id={id} {...register('legalName')} />}</Field>
            <Field label="ח.פ. / ע.מ." error={e.vatId?.message}>{(id) => <Input id={id} dir="ltr" className="text-start" inputMode="numeric" {...register('vatId')} />}</Field>
            <Field label="סוג העסק">
              {(id) => (
                <Select id={id} {...register('businessType')}>
                  <option value="">—</option>
                  {BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                  {org.businessType && !BUSINESS_TYPES.includes(org.businessType as never) && <option>{org.businessType}</option>}
                </Select>
              )}
            </Field>
            <Field label="מספר סניפים" error={e.branchesCount?.message}>{(id) => <Input id={id} type="number" min={1} {...numField('branchesCount')} />}</Field>
            <Field label="אתר">{(id) => <Input id={id} dir="ltr" className="text-start" {...register('website')} />}</Field>
            <Field label="כתובת">{(id) => <Input id={id} {...register('address')} />}</Field>
            <Field label="עיר">{(id) => <Input id={id} {...register('city')} />}</Field>
          </section>
          <section className="grid gap-4 rounded-card border border-line bg-sunken/40 p-4 sm:grid-cols-3">
            <Field label="מקור">
              {(id) => (
                <Select id={id} {...register('source')}>
                  {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="עדיפות">
              {(id) => (
                <Select id={id} {...register('priority')}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="אחראי">
              {(id) => (
                <Select id={id} {...register('ownerId', { setValueAs: (v) => v || null })}>
                  <option value="">לא משויך</option>
                  {users.data?.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                </Select>
              )}
            </Field>
          </section>
          <section className="grid gap-4 sm:grid-cols-2">
            <Field label="שווי משוער (₪)" error={e.estimatedValue?.message}>{(id) => <Input id={id} type="number" min={0} step="1" {...numField('estimatedValue')} />}</Field>
            <Field label="צריכה חודשית משוערת (ק״ג)" error={e.estimatedMonthlyKg?.message}>{(id) => <Input id={id} type="number" min={0} step="0.1" {...numField('estimatedMonthlyKg')} />}</Field>
            <Field label="הנחת לקוח קבועה (%)" error={e.defaultDiscountPct?.message} hint="חלה על מחירון ומדרגות כמות">
              {(id) => <Input id={id} type="number" min={0} max={100} step="0.5" {...numField('defaultDiscountPct')} />}
            </Field>
            <Field label="תנאי תשלום">{(id) => <Input id={id} {...register('paymentTerms')} placeholder="לדוגמה: שוטף + 30" />}</Field>
            <Field label="תנאי משלוח" className="sm:col-span-2">{(id) => <Input id={id} {...register('shippingTerms')} placeholder="לדוגמה: משלוח חינם מעל 2 ק״ג" />}</Field>
          </section>
          <Field label="הערה קבועה">{(id) => <Textarea id={id} rows={3} {...register('notes')} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
